import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { coerceKpiNumber } from "@/lib/coerce-numeric";
import type { OeePeriod, OeeResponse, MachineOeeBreakdownResponse } from "@opus-dt/shared-types";

function sanitizeOee(data: OeeResponse): OeeResponse {
  return {
    ...data,
    oee: coerceKpiNumber(data.oee, "oee.oee"),
    availability: coerceKpiNumber(data.availability, "oee.availability"),
    performance: coerceKpiNumber(data.performance, "oee.performance"),
    quality: coerceKpiNumber(data.quality, "oee.quality"),
    deltaPp: data.deltaPp === undefined ? undefined : coerceKpiNumber(data.deltaPp, "oee.deltaPp"),
    planAdherence:
      data.planAdherence == null
        ? data.planAdherence
        : {
            ...data.planAdherence,
            value: coerceKpiNumber(data.planAdherence.value, "oee.planAdherence"),
          },
  };
}

interface UseOeeOptions {
  refetchInterval?: number;
  compareWithPrevious?: boolean;
  breakdown?: boolean;
  window?: { from: string; to: string };
  enabled?: boolean;
}

export function useOee(lineId: string, period: OeePeriod = "24h", options: UseOeeOptions = {}) {
  const {
    refetchInterval = 60_000,
    compareWithPrevious = false,
    breakdown = false,
    window,
    enabled = true,
  } = options;

  const queryKey = window
    ? (["oee", lineId, "custom", window.from, window.to, compareWithPrevious, breakdown] as const)
    : (["oee", lineId, period, compareWithPrevious, breakdown] as const);

  return useQuery<OeeResponse>({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (window) {
        params.set("from", window.from);
        params.set("to", window.to);
      } else {
        params.set("period", period);
      }
      if (compareWithPrevious) params.set("compareWithPrevious", "true");
      if (breakdown) params.set("breakdown", "true");
      return api.get<OeeResponse>(`/api/v1/lines/${lineId}/oee?${params.toString()}`);
    },
    select: sanitizeOee,
    refetchInterval,
    enabled,
  });
}

// Decomposição do OEE por máquina (Disponibilidade + Qualidade + refugo). Key sob o
// prefixo ["oee", lineId] ⇒ varrida pela invalidação de OEE/realtime (OEE_UPDATED) e mutations.
export function useMachineOee(
  lineId: string,
  period: OeePeriod = "24h",
  options: { enabled?: boolean; refetchInterval?: number } = {}
) {
  const { enabled = true, refetchInterval = 60_000 } = options;
  return useQuery<MachineOeeBreakdownResponse>({
    queryKey: ["oee", lineId, "by-machine", period] as const,
    queryFn: () =>
      api.get<MachineOeeBreakdownResponse>(
        `/api/v1/lines/${lineId}/oee/by-machine?period=${period}`
      ),
    refetchInterval,
    enabled,
  });
}
